<template>
	<!-- DEV: to change the dropdown orientation when opened add is-upward class to the dropdown div -->
	<div
		:class="[
			'dropdown',
			isArrowUp ? 'is-upward' : dispatchDropUp ? 'is-upward' : '',
			required ? 'is-required' : '',
			hasError ? 'has-error' : '',
			disabled ? 'is-disabled' : ''
		]"
		:id="uid"
		:variant="variant"
		ref="dropdown"
	>
		<Label
			:for="`${uid}_dd`"
			class="dropdown__label"
			v-if="label"
			:labelText="label"
		/>
		<div
			class="dropdown__input-container"
			ref="dropdownPosRef"
			tabindex="0"
			v-on:keyup="handleKeyUp"
			v-on:keypress.enter.prevent
		>
			<div
				class="dropdown__selected"
				:id="`${uid}_dd`"
				@click="toggleMenu()"
				:required="required"
			>
				{{ selectedText || defaultValue }}
			</div>
			<div class="dropdown__trailing-container">
				<div v-if="showClearSelectedButton && selectedText">
					<Button
						className="btn--icon-only"
						:btnClick="clearValue"
						:iconBeforeLabel="true"
						:iconClassName="'icon-close'"
						:id="`${uid}_btn`"
						:variant="variant"
						v-if="selectedText !== defaultValue"
					/>
				</div>
				<div class="dropdown__trailing-item" v-show="showIErrorIcon">
					<div class="dropdown__icon dropdown__icon--error">
						<i class="icon icon-error-solid"></i>
					</div>
				</div>
				<div class="dropdown__trailing-item" @click="toggleMenu()">
					<div class="dropdown__icon dropdown__icon--arrow">
						<i
							:class="[
								'icon',
								menuOnTop ? 'icon-caret_up' : 'icon-caret_down'
							]"
						></i>
					</div>
				</div>
			</div>
		</div>

		<DropdownList
			ref="dropdownListRef"
			:autosuggestion="autosuggestion"
			:id="`${uid}_ddl`"
			:isOpen="isOpen"
			:multiSelect="multiSelect"
			:zone="zone"
			:options="items"
			:value="value"
			v-if="renderList"
			@change="updateValue"
		/>
		<div class="dropdown__text dropdown__text--error" v-if="hasError">
			{{ errorText || defaultErrorText }}
		</div>
		<HelpFeedback
			class="dropdown__text"
			:helpText="helpText"
			v-if="helpText"
			data-test="helpFeedback"
		/>

		<PopperPosition
			trackedRefName="dropdownPosRef"
			popoverRefName="dropdownListRef"
			placement="bottom-start"
			:popperVisible="isOpen"
			strategy="fixed"
			:altBoundary="altBoundary"
			@popperMinWidthChanged="setpopperMinWidth"
			@positionUpdated="updateIconOrientation"
		/>
	</div>
</template>
<script>
//import { ApiService as api } from '@/services/axiosHelper';

import Label from '@/components/forms/label/label';
import dropdownMixins from '@/mixins/dropdown';
import Button from '@/components/forms/button/button';
import { isObject, isArray } from '@/utils/inspect';
import PopperPosition from '@/components/visual/popper-position';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';

export default {
	name: 'Dropdown',
	inheritAttrs: false,
	mixins: [dropdownMixins],
	model: {
		prop: 'value',
		event: 'change'
	},
	components: {
		Label,
		Button,
		PopperPosition,
		HelpFeedback
	},
	props: {
		toggleList: {
			type: [Function]
		},
		//property for call api and populate items
		api: {
			type: Object,
			default: () => {
				return {
					url: '', //api url
					text: '', //response property that matches "text" property in dropdown
					value: '' //response property that matches "value" property in dropdown
				};
			}
		},
		isArrowUp: {
			type: Boolean,
			default: false
		},
		showIErrorIcon: {
			type: Boolean,
			default: true
		},
		altBoundary: {
			type: String,
			default: undefined
		}
	},
	data() {
		return {
			dispatchDropUp: null
		};
	},
	computed: {
		selectedText() {
			let text = '';

			if (!this.items.length) {
				return text;
			}

			if (![undefined, null].includes(this.value)) {
				text = this.value;
				let gettxt = val =>
					this.items
						.filter(item => item.value === val)
						.map(item => item.text);

				if (isArray(this.value)) {
					let fn = value => value.map(e => gettxt(e)[0]).join(', ');

					if (this.value.length > 1) {
						text = 'Multiple Selection';
					} else {
						text = fn(this.value);
					}
				} else {
					text = gettxt(this.value)[0];
				}
			}

			return text;
		}
	},
	created() {
		if (isObject(this.api) && this.api.url) {
			const options = this.callApi(this.api);
			if (options.length > 0) {
				this.items = options;
			}
		}
	},
	mounted() {
		if (this.toggleList instanceof Function) {
			this.toggleMenu = this.toggleList;
		}
		if (this.width && this.$refs.dropdownListRef) {
			this.$refs.dropdownListRef.$el.style.minWidth = `${this.width}px`;
		} else if (this.$refs.dropdownListRef) {
			this.$nextTick(() => {
				if (this.$refs.dropdownPosRef) {
					const dropdownShapeWidth = this.$refs.dropdownPosRef.getBoundingClientRect()
						.width;
					this.$refs.dropdownListRef.$el.style.minWidth = `${dropdownShapeWidth}px`;
				}
			});
		}
	},
	methods: {
		updateValue(val) {
			this.isOpen = false;
			this.$emit('change', val);
		},
		clearValue() {
			this.$emit('change', null);
		},
		// async callApi(data) {
		// 	const results = await api.post(data.url, null);
		// 	let options = [];

		// 	if (results && results.length > 0) {
		// 		options = results.map(item => ({
		// 			text: item[data.text || 'text'],
		// 			valye: item[data.value || 'value']
		// 		}));
		// 	}

		// 	return options;
		// },
		setpopperMinWidth(value) {
			this.popperMinWidth = value;
		}
	}
};
</script>

<style lang="scss">
/**
    * Dropdown styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.dropdown {
	position: relative;
	margin: 0;
	font-size: $input-font-size;

	&__input-container {
		position: relative;
		@include textfield;
		display: flex;

		&:focus {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__selected {
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		padding: $input-padding-y $input-padding-x;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		background-color: transparent;
		width: 100%;
		z-index: 1;
		white-space: nowrap;
		overflow: hidden;
	}

	&__trailing-container {
		display: flex;
		align-items: center;
		font-size: $input-font-size + 2;
		z-index: 0;
	}

	&__icon {
		display: flex;
		padding-right: $input-padding-x;
		cursor: pointer;

		&--arrow {
			font-size: 8px;
		}
	}

	&__icon--error {
		display: none;
	}

	select {
		option {
			&:checked {
				background-color: $neutral-4;
			}

			&:hover,
			&:active {
				background-color: lighten($neutral-4, 5%);
			}
		}

		+ .dropdown__trailing-container {
			position: absolute;
			right: $input-padding-x;
			top: $input-padding-y;

			.dropdown__icon--arrow {
				padding-right: 0;
				color: initial;
			}
		}
	}

	&.is-upward {
		.dropdown__icon--arrow {
			.icon {
				transform: rotate(180deg);
			}
		}
		.dropdown__list-container {
			bottom: 100%;
			top: initial;
		}
	}
}

/*------------------------------------*\
  #ERROR
\*------------------------------------*/

.has-error {
	.dropdown__input-container {
		border-color: $ui-negative;

		&:focus {
			outline-color: $ui-negative;
		}
	}

	.dropdown__icon--error,
	.dropdown__text--error {
		display: block;
		color: $ui-negative;
	}

	.dropdown__text--error {
		font-size: $label-font-size - 2;
		margin-top: $gutter-sm;
	}
}
</style>
